import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import LoginPage from './components/LoginPage';
import PortalShell from './components/PortalShell';
import SwilDocuments from './components/SwilDocuments';
import { portalLogout } from './services/swil';
import ProductSearch from './components/ProductSearch';
import CsvOrder from './components/CsvOrder';
import Cart from './components/Cart';
import ViewOrder from './components/ViewOrder';
import OrderDetail from './components/OrderDetail';
import api from './services/api';
import SalesmanDashboard from './components/SalesmanDashboard';
import { CustomerInsightsProvider, BackgroundSyncStatus } from './components/CustomerInsightsContext';

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [salesman, setSalesman] = useState(null);
  const [products, setProducts] = useState(null);


  const [restoring, setRestoring] = useState(true);
  useEffect(() => {
    let mounted = true;
    api.get('session.php').then(async ({ data }) => {
      if (!mounted) return;
      const profile = data.profile;
      setSalesman(profile);
      localStorage.setItem('salesman', JSON.stringify(profile));
      const { data: assigned } = await api.get('get_customers.php');
      if (!mounted) return;
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem('customer')); } catch { /* Invalid saved selection. */ }
      const selected = assigned.customers.find(item => String(item.customer_id) === String(saved?.customer_id));
      setCustomer(selected || null);
      if (!selected) localStorage.removeItem('cartItems');
      setIsLoggedIn(true);
      try { setCartItems(JSON.parse(localStorage.getItem('cartItems') || '[]')); } catch { setCartItems([]); }
    }).catch((error) => {
      if (!mounted) return;
      // Only the server saying "not signed in" ends the sign-in; a network hiccup keeps the saved one.
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem('salesman')); } catch { /* Invalid saved profile. */ }
      if (error.response?.status !== 401 && saved?.id) {
        let savedCustomer = null;
        try { savedCustomer = JSON.parse(localStorage.getItem('customer')); } catch { /* Invalid saved selection. */ }
        setSalesman(saved);
        setCustomer(savedCustomer || null);
        try { setCartItems(JSON.parse(localStorage.getItem('cartItems') || '[]')); } catch { setCartItems([]); }
        setIsLoggedIn(true);
        return;
      }
      localStorage.removeItem('customer');
      localStorage.removeItem('salesman');
      localStorage.removeItem('cartItems');
    }).finally(() => { if (mounted) setRestoring(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!restoring && isLoggedIn) localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems, restoring, isLoggedIn]);

  const handleLogin = (salesmanData) => {
    // The router mounts after login; start it on the customer directory.
    window.history.replaceState(window.history.state, '', '/salesman');
    setSalesman(salesmanData);
    setIsLoggedIn(true);
  };

  const handleLogout = async () => {
    try { await portalLogout(); } catch (error) { alert(error.message); return; }
    localStorage.removeItem('salesman');
    localStorage.removeItem('customer');
    localStorage.removeItem('cartItems');
    // Leave the portal first: outside a batched update, clearing the salesman would re-render the portal with no profile.
    setIsLoggedIn(false);
    setSalesman(null);
    setCustomer(null);
    setCartItems([]);
  };

  const handleAddToCart = (product) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.product_id === product.product_id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.product_id === product.product_id ? { ...item, quantity: product.quantity } : item
        );
      }
      return [...prevItems, product];
    });
  };

  const handleUpdateCartItem = (updatedProduct) => {
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.product_id === updatedProduct.product_id ? updatedProduct : item
      )
    );
  };

  const handleRemoveFromCart = (productId) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.product_id !== productId));
  };

  const handleClearCart = () => {
    setCartItems([]);
  };

  const cartItemCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  if (restoring) return <Box p={3}>Restoring session…</Box>;

  if (!isLoggedIn || !salesman) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <Router>
      <CustomerInsightsProvider key={salesman.id} salesman={salesman}>
      <PortalShell role="salesman" customer={customer} salesman={salesman} cartCount={cartItemCount} onLogout={handleLogout}>
            <BackgroundSyncStatus />
            <Routes>
              <Route path="/ledger" element={<SwilDocuments kind="ledger" customer={customer} />} />
              <Route path="/invoices" element={<SwilDocuments kind="invoices" customer={customer} />} />
              <Route path="/credit-notes" element={<SwilDocuments kind="credit-notes" customer={customer} />} />
              <Route path="/account-statement" element={<SwilDocuments kind="outstanding" customer={customer} />} />
              <Route path="/" element={<Navigate to="/salesman" salesman={salesman} />} /> {/* Redirect root to home */}
              <Route path="/salesman" element={<SalesmanDashboard salesman={salesman} onSelectCustomer={(selected) => {
                if (String(customer?.customer_id) !== String(selected.customer_id)) setCartItems([]);
                setCustomer(selected);
              }} />} /> {/* Default Home Page */}
              <Route path="/search" element={<ProductSearch onAddToCart={handleAddToCart} cartItems={cartItems} products={products} setProducts={setProducts}  onRemoveFromCart={handleRemoveFromCart} onUpdateCartItem={handleUpdateCartItem} customer={customer} onClearCart={handleClearCart} />} />
              <Route path="/csv-order" element={<CsvOrder customer={customer} products={products} setProducts={setProducts} cartItems={cartItems} onAddToCart={handleAddToCart} />} />
              <Route path="/cart" element={<Cart cartItems={cartItems} onRemoveFromCart={handleRemoveFromCart} onUpdateCartItem={handleUpdateCartItem} customer={customer} onClearCart={handleClearCart} />} />
              <Route path="/orders" element={<ViewOrder customer={customer} />} />
              <Route path="/order/:orderId" element={<OrderDetail customer={customer} />} />

              <Route path="*" element={<Navigate to="/salesman" />} /> {/* Redirect all unknown paths to My Dashboard */}
            </Routes>
      </PortalShell>
      </CustomerInsightsProvider>
    </Router>
  );
};

export default App;
